/**
 * Timeline curved connector — GSAP + ScrollTrigger
 * Layout (image + text columns) stays unchanged; SVG overlay only.
 */
(function () {
  var GSAP_SRC = 'https://cdn.jsdelivr.net/npm/gsap@3.12.7/dist/gsap.min.js';
  var ST_SRC = 'https://cdn.jsdelivr.net/npm/gsap@3.12.7/dist/ScrollTrigger.min.js';
  var instances = new WeakMap();

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function ensureGsap() {
    return loadScript(GSAP_SRC)
      .then(function () { return loadScript(ST_SRC); })
      .then(function () {
        if (window.gsap && window.ScrollTrigger) {
          window.gsap.registerPlugin(window.ScrollTrigger);
        }
      });
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function buildSmoothPath(points, trackWidth) {
    if (points.length < 2) return '';
    var centerX = trackWidth * 0.5;
    /* Dezente Welle um die Mittelachse — kompakt, nicht an Labels gebunden */
    var bulge = Math.min(18, trackWidth * 0.022);
    var d = 'M ' + points[0].x + ' ' + points[0].y;

    for (var i = 0; i < points.length - 1; i++) {
      var p0 = points[i];
      var p1 = points[i + 1];
      var midY = (p0.y + p1.y) * 0.5;
      var wave = i % 2 === 0 ? bulge : -bulge;
      var c1x = centerX + wave;
      var c2x = centerX - wave;
      d += ' C ' + c1x + ' ' + midY + ', ' + c2x + ' ' + midY + ', ' + p1.x + ' ' + p1.y;
    }
    return d;
  }

  function TimelineConnector(root) {
    this.root = root;
    this.track = root.querySelector('[data-timeline-track]');
    this.svg = root.querySelector('[data-timeline-svg]');
    this.pathEl = root.querySelector('[data-timeline-path]');
    this.dotsEl = root.querySelector('[data-timeline-dots]');
    this.scrollTrigger = null;
    this.resizeObserver = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.resizeHandler = debounce(this.refresh.bind(this), 150);
    this.dotRadius = window.matchMedia('(max-width: 767px)').matches ? 3.5 : 4;
    this.pathLength = 0;
    this.lastGeometry = null;
    this.progressProxy = null;
  }

  TimelineConnector.prototype.getCenterX = function (trackWidth) {
    var mobile = window.matchMedia('(max-width: 767px)').matches;
    /* Desktop: mittig zwischen Bild- und Textspalten */
    return mobile ? trackWidth * 0.08 : trackWidth * 0.5;
  };

  TimelineConnector.prototype.getScrollEnd = function () {
    var track = this.track;
    var h = track.offsetHeight;
    if (h < 1) return 'bottom 55%';

    var items = track.querySelectorAll('.timeline--item');
    if (!items.length) return 'bottom 55%';

    var last = items[items.length - 1];
    var trackRect = track.getBoundingClientRect();
    var lastRect = last.getBoundingClientRect();
    /* Unterkante des letzten Eintrags — Linie fertig, bevor die Sektion rausscrollt */
    var lastY = lastRect.bottom - trackRect.top + 32;
    var pct = Math.min(96, Math.max(25, (lastY / h) * 100));

    return pct.toFixed(1) + '% 58%';
  };

  TimelineConnector.prototype.getAnchors = function () {
    var track = this.track;
    var trackRect = track.getBoundingClientRect();
    var trackWidth = track.offsetWidth;
    var centerX = this.getCenterX(trackWidth);
    var items = track.querySelectorAll('.timeline--item');
    var anchors = [];

    items.forEach(function (item) {
      var r = item.getBoundingClientRect();
      anchors.push({
        x: centerX,
        y: r.top + r.height * 0.5 - trackRect.top
      });
    });

    return anchors;
  };

  TimelineConnector.prototype.renderDots = function (points) {
    if (!this.dotsEl) return;
    var self = this;
    var circles = this.dotsEl.children;

    /* Vorhandene Kreise weiterverwenden — innerHTML-Neuaufbau lässt die Punkte blinken */
    while (circles.length > points.length) {
      this.dotsEl.removeChild(this.dotsEl.lastChild);
    }

    points.forEach(function (p, i) {
      var circle = circles[i];
      if (!circle) {
        circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('class', 'timeline--connector__dot');
        self.dotsEl.appendChild(circle);
      }
      circle.setAttribute('cx', String(p.x));
      circle.setAttribute('cy', String(p.y));
      circle.setAttribute('r', String(self.dotRadius));
      circle.setAttribute('data-index', String(i));
    });
  };

  TimelineConnector.prototype.setOffset = function (progress) {
    var len = this.pathLength || 0;
    var p = Math.max(0, Math.min(1, progress || 0));
    this.pathEl.style.strokeDashoffset = String(len * (1 - p));
  };

  TimelineConnector.prototype.build = function () {
    if (!this.track || !this.svg || !this.pathEl) return false;

    var w = this.track.offsetWidth;
    var h = this.track.offsetHeight;
    if (w < 1 || h < 1) return false;

    var points = this.getAnchors();
    if (points.length < 2) {
      this.pathEl.setAttribute('d', '');
      if (this.dotsEl) this.dotsEl.innerHTML = '';
      this.lastGeometry = null;
      return false;
    }

    var d = buildSmoothPath(points, w);
    var geometry = w + 'x' + h + '|' + d;

    /* Lazy-Bilder lösen den ResizeObserver mehrfach aus; ohne Geometrieänderung nichts anfassen */
    if (geometry === this.lastGeometry) return false;
    this.lastGeometry = geometry;

    this.svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    this.svg.setAttribute('width', String(w));
    this.svg.setAttribute('height', String(h));
    this.pathEl.setAttribute('d', d);
    this.renderDots(points);

    this.pathLength = this.pathEl.getTotalLength();
    this.pathEl.style.strokeDasharray = String(this.pathLength);

    if (this.reducedMotion) {
      this.pathEl.style.strokeDashoffset = '0';
      return true;
    }

    /* Fortschritt erhalten: sonst springt die Linie beim Neuaufbau auf unsichtbar zurück */
    this.setOffset(this.scrollTrigger ? this.scrollTrigger.progress : 0);
    this.animate();
    return true;
  };

  TimelineConnector.prototype.animate = function () {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;
    if (this.reducedMotion) return;

    /* Trigger nur einmal anlegen — Neuaufbau würde die Scrub-Glättung bei 0 neu starten */
    if (this.scrollTrigger) return;

    var self = this;
    this.progressProxy = { p: 0 };

    this.scrollTrigger = ScrollTrigger.create({
      trigger: this.track,
      start: 'top 76%',
      end: this.getScrollEnd.bind(this),
      scrub: 0.5,
      animation: gsap.fromTo(
        this.progressProxy,
        { p: 0 },
        {
          p: 1,
          ease: 'none',
          duration: 1,
          immediateRender: false,
          onUpdate: function () {
            self.setOffset(self.progressProxy.p);
          }
        }
      )
    });
  };

  TimelineConnector.prototype.refresh = function () {
    var changed = this.build();
    if (changed && window.ScrollTrigger) window.ScrollTrigger.refresh();
  };

  TimelineConnector.prototype.watchLayout = function () {
    var self = this;
    window.addEventListener('resize', this.resizeHandler);

    if (window.ResizeObserver && this.track) {
      this.resizeObserver = new ResizeObserver(this.resizeHandler);
      this.resizeObserver.observe(this.track);
    }

    this.track.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', self.resizeHandler, { once: true });
    });

    window.addEventListener('load', this.resizeHandler, { once: true });
    setTimeout(this.resizeHandler, 400);
  };

  TimelineConnector.prototype.destroy = function () {
    window.removeEventListener('resize', this.resizeHandler);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
      this.scrollTrigger = null;
    }
    if (window.gsap) {
      window.gsap.killTweensOf(this.pathEl);
      if (this.progressProxy) window.gsap.killTweensOf(this.progressProxy);
    }
    this.progressProxy = null;
    this.pathLength = 0;
    this.lastGeometry = null;
    if (this.dotsEl) this.dotsEl.innerHTML = '';
    if (this.pathEl) {
      this.pathEl.removeAttribute('d');
      this.pathEl.style.strokeDasharray = '';
      this.pathEl.style.strokeDashoffset = '';
    }
  };

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    if (root.getAttribute('data-timeline-connector') !== 'true') return;

    var connector = new TimelineConnector(root);

    ensureGsap()
      .then(function () {
        instances.set(root, connector);
        requestAnimationFrame(function () {
          connector.build();
          connector.watchLayout();
        });
      })
      .catch(function () {
        /* GSAP failed — line stays hidden */
      });
  }

  function destroyRoot(root) {
    var connector = instances.get(root);
    if (!connector) return;
    connector.destroy();
    instances.delete(root);
  }

  function initAll() {
    document.querySelectorAll('.timeline--root[data-timeline-connector="true"]').forEach(initRoot);
  }

  function onSectionLoad(e) {
    var sectionId = e.detail && e.detail.sectionId;
    if (!sectionId) return;
    var root = document.querySelector(
      '.timeline--root[data-section-id="' + sectionId + '"][data-timeline-connector="true"]'
    );
    if (!root) return;
    destroyRoot(root);
    requestAnimationFrame(function () { initRoot(root); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', function (e) {
    var sectionId = e.detail && e.detail.sectionId;
    if (!sectionId) return;
    var root = document.querySelector('.timeline--root[data-section-id="' + sectionId + '"]');
    if (root) destroyRoot(root);
  });
})();
